import React from 'react';
import { createPortal } from 'react-dom';
import type { TeamUserWithPresence } from '@troia-v3/types';
import { Icon } from '@/shared/icons'

const PANEL_WIDTH = 280;

interface Props {
  /** Ref to the trigger button (sidebar footer) used to anchor the fixed panel. */
  triggerRef: React.RefObject<HTMLButtonElement>;
  users: TeamUserWithPresence[];
  isLoading: boolean;
  unreadByUser: Record<string, number>;
  onClose: () => void;
  onUserClick: (user: TeamUserWithPresence) => void;
}

export const TeamChatDropdown: React.FC<Props> = ({
  triggerRef,
  users,
  isLoading,
  unreadByUser,
  onClose,
  onUserClick,
}) => {
  const [search, setSearch] = React.useState('');
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(null);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  const onlineCount = users.filter((u) => u.isOnline).length;

  // Position the portaled panel with position:fixed — opens UP and to the RIGHT
  // of the trigger, clamped inside the viewport. Runs before paint (useLayoutEffect)
  // and re-runs when content-driven height changes or on resize/scroll.
  React.useLayoutEffect(() => {
    const computePosition = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const rect = trigger.getBoundingClientRect();
      const panelWidth = panel.offsetWidth || PANEL_WIDTH;
      const panelHeight = panel.offsetHeight;
      // Flyout à direita da SIDEBAR (estilo submenu) quando o trigger vive nela; senão à direita do ícone.
      const sidebarRoot = trigger.closest('[data-sidebar-root]');
      const anchorRight = sidebarRoot ? sidebarRoot.getBoundingClientRect().right : rect.right;
      let left = Math.min(anchorRight + 8, window.innerWidth - panelWidth - 8);
      left = Math.max(left, 8);
      // Open UP: the panel base sits near the trigger, clamped to the viewport.
      let top = rect.top;
      top = Math.min(Math.max(top, 8), window.innerHeight - panelHeight - 8);
      setPos({ left, top });
    };
    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [triggerRef, isLoading, users.length, filtered.length, search]);

  // Outside-click: works across the portal by checking both the trigger and the
  // panel refs (the panel lives in document.body, not inside the trigger's DOM).
  // The trigger is excluded so its own onClick can toggle the panel closed.
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef]);

  return createPortal(
    /* Panel — portaled to document.body, position:fixed, opens up/right. */
    <div
      ref={panelRef}
      className="rounded-lg shadow-xl"
      style={{
        position: 'fixed',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        zIndex: 1000,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        width: PANEL_WIDTH,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px 10px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Equipe</span>
          {onlineCount > 0 && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                color: '#22c55e',
                fontWeight: 500,
              }}
            >
              {onlineCount} online
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}
        >
          <Icon name="x" size={15} />
        </button>
      </div>

      {/* Search */}
      {users.length > 5 && (
        <div style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              backgroundColor: '#f8fafc',
              borderRadius: 8,
              padding: '5px 10px',
            }}
          >
            <Icon name="search" size={13} style={{ color: '#94a3b8' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: 12,
                color: '#1e293b',
                width: '100%',
              }}
              autoFocus
            />
          </div>
        </div>
      )}

      {/* User list */}
      <div style={{ maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
        {isLoading && (
          <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, padding: '20px 0' }}>
            Carregando...
          </p>
        )}
        {!isLoading && filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, padding: '20px 0' }}>
            Nenhum usuário encontrado
          </p>
        )}
        {filtered.map((user) => {
          const unread = unreadByUser[user.id] || 0;
          return (
            <button
              key={user.id}
              onClick={() => { onUserClick(user); onClose(); }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 14px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {/* Avatar */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.firstName}
                    style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      backgroundColor: '#ede9fe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#7c3aed',
                    }}
                  >
                    {user.firstName[0]}{user.lastName[0]}
                  </div>
                )}
                {/* Presence dot */}
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    border: '1.5px solid #ffffff',
                    backgroundColor: user.isOnline ? '#22c55e' : '#cbd5e1',
                  }}
                />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#1e293b',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.firstName} {user.lastName}
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: user.isOnline ? '#22c55e' : '#94a3b8',
                    margin: 0,
                    fontWeight: user.isOnline ? 500 : 400,
                  }}
                >
                  {user.isOnline ? 'Online' : 'Offline'}
                </p>
              </div>

              {/* Unread badge */}
              {unread > 0 && (
                <span
                  style={{
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    flexShrink: 0,
                  }}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
};
